import gcoord from "gcoord";
import * as turf from "@turf/turf";
import china from "assets/json/c1.json";

const CHINA_LNG_MIN = 72.004;
const CHINA_LNG_MAX = 137.8347;
const CHINA_LAT_MIN = 0.8293;
const CHINA_LAT_MAX = 55.8271;

const chinaGeojson = turf.polygon(china.features[0].geometry.rings);

/**
 * 地图业务中使用的坐标系类型：
 * - GCJ-02：高德/国测坐标，地图 SDK 绘制通常直接使用该坐标。
 * - WGS84：GPS 原始坐标，境内上图前通常需要转换为 GCJ-02。
 */
export type CoordinateSystem = "GCJ02" | "WGS84";

/**
 * 判断一个经纬度点是否在中国大陆近似范围外。
 *
 * 说明：
 * 1) GCJ02 偏移模型只对中国大陆范围内点位生效。
 * 2) 范围外坐标若强行套用偏移公式会产生错误偏移，因此必须先做边界短路。
 * 3) 这里沿用业内常见的经验边界值，以保持与历史逻辑一致。
 */
export const isOutOfChina = (lng: number, lat: number): boolean => {
  const outOfApproxBounds =
    lng < CHINA_LNG_MIN ||
    lng > CHINA_LNG_MAX ||
    lat < CHINA_LAT_MIN ||
    lat > CHINA_LAT_MAX;

  // 先用矩形边界快速短路，再用中国边界多边形做精确判定。
  if (outOfApproxBounds) {
    return true;
  }

  return !turf.booleanPointInPolygon([lng, lat], chinaGeojson);
};

/**
 * 将单个点从 WGS84 转换到 GCJ-02。
 *
 * 关键约束：
 * 1) 非法点（非数组、长度不足、经纬度非数字）会原样返回，避免抛异常中断批量流程。
 * 2) 中国大陆范围外点位原样返回，保持“境外不偏移”业务规则。
 * 3) 如果点位携带海拔等扩展维度（第 3 维及之后），会被完整保留。
 */
export const wgs84ToGcj02Point = (point: number[]): number[] => {
  if (!Array.isArray(point) || point.length < 2) {
    return point;
  }

  const lng = point[0];
  const lat = point[1];
  if (typeof lng !== "number" || typeof lat !== "number") {
    return point;
  }

  if (isOutOfChina(lng, lat)) {
    return point;
  }

  const [mgLng, mgLat] = gcoord.transform(
    [lng, lat],
    gcoord.WGS84,
    gcoord.GCJ02
  ) as [number, number];

  return [mgLng, mgLat, ...point.slice(2)];
};

/**
 * 将线段路径转换为地图绘制坐标。
 *
 * 业务约定：
 * 1) 当输入坐标系为 WGS84 时，逐点转换为 GCJ02 后再交给地图绘制。
 * 2) 当输入为 GCJ02 或未声明坐标系时，默认认为已是可绘制坐标，直接返回原值。
 * 3) 返回新数组（WGS84 分支）可避免意外修改原始数据引用。
 */
export const getMapPathCoordinates = (
  coordinates: number[][],
  shapeCoordinateSystem?: CoordinateSystem
): number[][] => {
  if (shapeCoordinateSystem !== "WGS84") {
    return coordinates;
  }

  return coordinates.map((point) => wgs84ToGcj02Point(point));
};

/**
 * 将线段路径转换为地图绘制坐标。
 * 如果 shapeCoordinateSystem 是 "WGS84"，将中国境内的坐标转换为 GCJ-02，境外坐标保持不变。
 * 其他坐标系（含未声明）按已可绘制坐标处理，原样返回。
 */

export const getMapPathCoordinatesFromWgs84 = (
  coordinates: number[][],
  shapeCoordinateSystem?: CoordinateSystem
): number[][] => {
  // 仅在显式声明 WGS84 时执行纠偏；其余场景按“已是地图坐标”处理。
  if (shapeCoordinateSystem !== "WGS84") {
    return coordinates;
  }

  return coordinates.map((point) => {
    // 容错：异常点位原样透传，避免整条线转换失败。
    if (!Array.isArray(point) || point.length < 2) {
      return point;
    }

    const lng = point[0];
    const lat = point[1];
    if (typeof lng !== "number" || typeof lat !== "number") {
      return point;
    }

    // 境外坐标不参与 GCJ02 偏移，保持原值。
    if (isOutOfChina(lng, lat)) {
      return point;
    }

    const [mgLng, mgLat] = gcoord.transform(
      [lng, lat],
      gcoord.WGS84,
      gcoord.GCJ02
    ) as [number, number];

    // 保留第 3 维及之后的扩展信息（如高程）。
    return [mgLng, mgLat, ...point.slice(2)];
  });
};
